/**
 * useBanks Hook
 * ahooks data fetching hook for vendor bank accounts and bank lookup
 */

import { useRequest } from 'ahooks';
import type { Options } from 'ahooks/lib/useRequest/src/types';
import { bankApi } from '@/services/api';
import { useBankStore, useBankAccountsForVendor, useBankPaginationForVendor } from '@/stores';
import type {
  BankAccountListResponse,
  BankAccountListParams,
  BankSearchParams,
  BankSearchResponse,
  BranchSearchParams,
  BranchSearchResponse,
} from '@/types/api';
import { getStoreFirstArray, getStoreFirstValue, getCacheKey } from '@/utils/storeFirstData';
import { trackCacheKey, clearCacheWhere } from '@/utils/cacheKeyRegistry';

/**
 * Helper to invalidate all bank account caches for a vendor
 */
const invalidateBankCaches = (vendorId: number) => {
  // Exact-key clear of the tracked bank keys (the old predicate form was a
  // silent no-op; see cacheKeyRegistry.ts)
  clearCacheWhere((key) => key.includes(`/api/vendors/${vendorId}/banks`));
};

/**
 * Hook to fetch bank accounts for a specific vendor
 */
export function useBankAccounts(
  vendorId: number | null,
  params?: Omit<BankAccountListParams, 'vendorId'>,
  config?: Options<BankAccountListResponse, []>
) {
  const { setBankAccountsForVendor, setPaginationForVendor, setLoading, setError } = useBankStore();

  // Fetch bank accounts with ahooks
  const { data, error, loading, refresh, mutate } = useRequest(
    () => bankApi.list(vendorId!, params),
    {
      ready: !!vendorId,
      refreshDeps: [vendorId, JSON.stringify(params)],
      cacheKey: vendorId
        ? trackCacheKey(getCacheKey(`/api/vendors/${vendorId}/banks`, params))
        : undefined,
      staleTime: 5000, // 5 seconds - reduced for better responsiveness after mutations
      ...config,
      onSuccess: (data) => {
        if (vendorId) {
          setBankAccountsForVendor(vendorId, data.data);
          setPaginationForVendor(vendorId, data.pagination);
          setLoading(false);
          setError(null);
        }
        config?.onSuccess?.(data, []);
      },
      onError: (err) => {
        setLoading(false);
        setError(err.message || 'Failed to fetch bank accounts');
        config?.onError?.(err, []);
      },
    }
  );

  // Get bank accounts from store for immediate access
  // Always call hooks unconditionally (React Rules of Hooks)
  // Use 0 as fallback when vendorId is null (will return empty array from store)
  const bankAccountsFromStore = useBankAccountsForVendor(vendorId ?? 0);
  const paginationFromStore = useBankPaginationForVendor(vendorId ?? 0);

  const bankAccounts = vendorId ? bankAccountsFromStore : [];
  const pagination = vendorId ? paginationFromStore : undefined;

  // Store-first pattern: prioritize store data for instant UI updates after mutations
  return {
    bankAccounts: getStoreFirstArray(bankAccounts, data?.data),
    pagination: getStoreFirstValue(pagination, data?.pagination),
    error,
    isLoading: loading,
    mutate, // mutate revalidates with cache
    refresh, // refresh forces a new request
  };
}

/**
 * Hook for bank account mutations (create, update, toggle status, set default)
 * All mutations invalidate ahooks cache to ensure UI stays in sync
 */
export function useBankAccountMutations(vendorId: number) {
  const {
    addBankAccount,
    updateBankAccount: updateBankAccountInStore,
    setDefaultBankAccount,
  } = useBankStore();

  const createBankAccount = async (data: Parameters<typeof bankApi.create>[1]) => {
    const response = await bankApi.create(vendorId, data);
    addBankAccount(vendorId, response);
    // Invalidate cache to refresh list
    invalidateBankCaches(vendorId);
    return response;
  };

  const updateBankAccount = async (
    bankAccountId: number,
    data: Parameters<typeof bankApi.update>[2]
  ) => {
    const response = await bankApi.update(vendorId, bankAccountId, data);
    updateBankAccountInStore(vendorId, bankAccountId, response);
    // Invalidate cache to refresh list
    invalidateBankCaches(vendorId);
    return response;
  };

  const toggleBankAccountStatus = async (bankAccountId: number, isActive: boolean) => {
    const response = await bankApi.toggleStatus(vendorId, bankAccountId, isActive);
    updateBankAccountInStore(vendorId, bankAccountId, response);
    // Invalidate cache to refresh list with updated status
    invalidateBankCaches(vendorId);
    return response;
  };

  const setDefaultAccount = async (bankAccountId: number, isDefault: boolean) => {
    const response = await bankApi.setDefault(vendorId, bankAccountId, isDefault);
    if (isDefault) {
      setDefaultBankAccount(vendorId, bankAccountId);
    }
    // Invalidate cache to refresh list with updated default
    invalidateBankCaches(vendorId);
    return response;
  };

  return {
    createBankAccount,
    updateBankAccount,
    toggleStatus: toggleBankAccountStatus,
    setDefault: setDefaultAccount,
  };
}

/**
 * Hook to search banks (for bank lookup dropdown)
 */
export function useBankSearch(params: BankSearchParams, config?: Options<BankSearchResponse, []>) {
  const { data, error, loading, refresh } = useRequest(() => bankApi.searchBanks(params), {
    ready: !!params.countryId && (!!params.code || !!params.bankName),
    debounceWait: 300,
    refreshDeps: [JSON.stringify(params)],
    cacheKey: params.code || params.bankName ? `/api/banks?${JSON.stringify(params)}` : undefined,
    cacheTime: 300000, // 5 minutes
    ...config,
  });

  return {
    banks: data?.data ?? [],
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}

/**
 * Hook to search branches for a specific bank
 */
export function useBranchSearch(
  params: BranchSearchParams,
  config?: Options<BranchSearchResponse, []>
) {
  const { data, error, loading, refresh } = useRequest(() => bankApi.searchBranches(params), {
    ready: !!params.countryId && !!params.bankId && (!!params.branchCode || !!params.branchName),
    debounceWait: 300,
    refreshDeps: [JSON.stringify(params)],
    cacheKey:
      params.branchCode || params.branchName
        ? `/api/banks/branches?${JSON.stringify(params)}`
        : undefined,
    cacheTime: 300000, // 5 minutes
    ...config,
  });

  return {
    branches: data?.data ?? [],
    error,
    isLoading: loading,
    mutate: refresh,
    refresh,
  };
}
